import React, { useContext, useState } from 'react'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { isArchivedWorldCheckScreeningStatus } from 'api/WorldCheck/Types/ScreeningResultsDto'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import styled from 'styled-components'
import { PageQuery } from 'utils/api'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WorldCheckApiClient } from 'utils/api/worldCheckApi'
import { formatLocalDateUTC } from 'utils/date'

const Container = styled.div`
  margin: 2.5rem;

  .link-type {
    text-decoration: underline;
    cursor: pointer;
  }
`

interface OuterProps {
  clientId: string
  typeId: number
  worldCheckApiClient: WorldCheckApiClient
  onWorldCheckScreeningModelOpen(caseId: string): void
}

export const ClientDetailsWorldCheckHistory: React.FC<OuterProps> = ({
  clientId,
  worldCheckApiClient,
  onWorldCheckScreeningModelOpen,
  typeId,
}) => {
  const setToast = useContext(ToastContext)
  const [wordCheckArchiveModalState, setWordArchiveModalState] = useState<{
    visible: boolean
    worldCheckRecordId: string
  }>({
    visible: false,
    worldCheckRecordId: '',
  })

  const { callback: fetchWorldCheckHistory, forceRefresh } = useCallbackWithForceRefresh(
    (query?: PageQuery) => worldCheckApiClient.getWorldCheckRecords(clientId, typeId, { ...query }),
    [clientId]
  )
  const { data, isLoading } = useFetchOne(fetchWorldCheckHistory)

  const handleWordCheckArchiveProceed = async () => {
    try {
      await worldCheckApiClient.archiveWorldCheckRecords(
        wordCheckArchiveModalState.worldCheckRecordId
      )
      setWordArchiveModalState({
        visible: false,
        worldCheckRecordId: '',
      })
      forceRefresh()
      setToast(successToast('Case has been successfully archived.'))
    } catch (e) {
      console.error('Error archiving world check record', e)
      setWordArchiveModalState({
        visible: false,
        worldCheckRecordId: '',
      })
    }
  }

  const handleWordCheckArchiveModalOpen = async (worldCheckRecordId: string) => {
    setWordArchiveModalState({
      visible: true,
      worldCheckRecordId,
    })
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {wordCheckArchiveModalState.visible && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleWordCheckArchiveProceed}
          onCancel={() =>
            setWordArchiveModalState({
              visible: false,
              worldCheckRecordId: '',
            })
          }
          visible={wordCheckArchiveModalState.visible}
          renderTitle={<div>Are you sure you want to archive this WordCheck Case?</div>}
        />
      )}
      <Container>
        <Grid data={data?.items} total={data?.itemsCount} resizable style={{ zIndex: 0 }}>
          <Column
            title='Case'
            cell={(gridCellProps: GridCellProps) => (
              <td className='link-type'>
                <span
                  onClick={() => {
                    onWorldCheckScreeningModelOpen(gridCellProps.dataItem.id)
                  }}
                >
                  {gridCellProps.dataItem.caseId}
                </span>
              </td>
            )}
          />
          <Column
            title='Status'
            cell={(gridCellProps: GridCellProps) => <td>{gridCellProps.dataItem.status.name}</td>}
          />
          <Column
            title='Ongoing Monitoring'
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <input
                  disabled
                  type='checkbox'
                  checked={gridCellProps.dataItem.ongoingScreeningEnabled}
                />
              </td>
            )}
          />
          <Column
            title='Created At'
            cell={(gridCellProps: GridCellProps) => (
              <td className='transaction-columns__date'>
                {formatLocalDateUTC(gridCellProps.dataItem.createdAt, true)}
              </td>
            )}
          />
          <Column
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Archive',
                      disabled: isArchivedWorldCheckScreeningStatus(
                        gridCellProps.dataItem.status.id
                      ),
                      onClick: () => handleWordCheckArchiveModalOpen(gridCellProps.dataItem.id),
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </Container>
    </React.Fragment>
  )
}
